import { useCallback, useEffect, useState } from 'react';
import {
  customergrowthApi,
  type CustomerGrowthDashboard,
  type CustomerHealthRecord,
  type EngagementCampaignRecord,
  type FollowerRecord,
  type GrowthRecommendation,
  type QuestionRecord,
  type RatingRecord,
  type ReviewRecord,
  type VineEnrollmentRecord,
} from '../../api/customergrowth';

type Tab = 'dashboard' | 'reviews' | 'ratings' | 'qa' | 'vine' | 'followers' | 'campaigns' | 'health' | 'recommendations';

function statusClass(s: string) {
  if (s === 'published' || s === 'answered' || s === 'completed' || s === 'active') return 'text-emerald-700 bg-emerald-50';
  if (s === 'pending' || s === 'in_progress' || s === 'eligible' || s === 'updated') return 'text-amber-700 bg-amber-50';
  if (s === 'removed' || s === 'rejected' || s === 'ignored') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function sentimentClass(s?: string) {
  if (s === 'positive') return 'text-emerald-700 bg-emerald-50';
  if (s === 'negative') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function scoreClass(score: number) {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 60) return 'text-amber-700';
  return 'text-red-700';
}

function severityClass(s?: string) {
  if (s === 'critical') return 'text-red-700 bg-red-50';
  if (s === 'high') return 'text-orange-700 bg-orange-50';
  if (s === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

function stars(rating: number) {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
}

export function CustomerGrowthPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<CustomerGrowthDashboard | null>(null);
  const [reviews, setReviews] = useState<ReviewRecord[]>([]);
  const [ratings, setRatings] = useState<RatingRecord[]>([]);
  const [questions, setQuestions] = useState<QuestionRecord[]>([]);
  const [vine, setVine] = useState<VineEnrollmentRecord[]>([]);
  const [followers, setFollowers] = useState<FollowerRecord[]>([]);
  const [campaigns, setCampaigns] = useState<EngagementCampaignRecord[]>([]);
  const [health, setHealth] = useState<CustomerHealthRecord[]>([]);
  const [recommendations, setRecommendations] = useState<GrowthRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, rev, rat, qs, vn, fol, camp, hlth, recs] = await Promise.all([
        customergrowthApi.dashboard(), customergrowthApi.listReviews(), customergrowthApi.listRatings(),
        customergrowthApi.listQuestions(), customergrowthApi.listVine(), customergrowthApi.listFollowers(),
        customergrowthApi.listCampaigns(), customergrowthApi.listHealth(), customergrowthApi.listRecommendations(),
      ]);
      setDashboard(dash); setReviews(rev); setRatings(rat); setQuestions(qs);
      setVine(vn); setFollowers(fol); setCampaigns(camp); setHealth(hlth); setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load customer growth data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await customergrowthApi.generateRecommendations(); await refresh(); } finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Growth Dashboard' },
    { id: 'reviews', label: 'Review Center' },
    { id: 'ratings', label: 'Ratings' },
    { id: 'qa', label: 'Q&A Center' },
    { id: 'vine', label: 'Vine Console' },
    { id: 'followers', label: 'Followers' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'health', label: 'Customer Intelligence' },
    { id: 'recommendations', label: 'AI Recommendations' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Customer Growth &amp; Engagement</h1>
          <p className="text-sm text-slate-500">Reviews, Vine, Q&amp;A, engagement campaigns &amp; AI customer copilot — APP-00006</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">Refresh</button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Total Reviews', value: dashboard.total_reviews.toString() },
            { label: 'Avg Rating', value: dashboard.avg_rating.toFixed(1) },
            { label: 'Pending Questions', value: dashboard.pending_questions.toString() },
            { label: 'Active Vine', value: dashboard.active_vine_enrollments.toString() },
            { label: 'Brand Followers', value: dashboard.brand_followers.toLocaleString() },
            { label: 'Active Campaigns', value: dashboard.active_campaigns.toString() },
            { label: 'Avg Customer Health', value: dashboard.avg_customer_health.toFixed(0) },
            { label: 'Avg Engagement', value: dashboard.avg_engagement_score.toFixed(0) },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'reviews' && (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-amber-500">{stars(r.rating)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${sentimentClass(r.sentiment)}`}>{r.sentiment}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(r.status)}`}>{r.status}</span>
                {r.verified && <span className="text-xs text-sky-600">Verified Purchase</span>}
              </div>
              {r.title && <h3 className="mt-1 font-medium text-slate-900">{r.title}</h3>}
              {r.body && <p className="mt-1 text-sm text-slate-600">{r.body}</p>}
              <div className="mt-2 flex gap-4 text-xs text-slate-400">
                {r.sku && <span>{r.sku}</span>}
                <span>{r.helpful_votes} helpful</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'ratings' && (
        <div className="space-y-3">
          {ratings.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{r.sku}</span>
                <span className="text-lg font-semibold text-amber-500">{r.avg_rating.toFixed(1)}</span>
                <span className="text-sm text-slate-500">{r.total_reviews.toLocaleString()} reviews</span>
              </div>
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                <span className="text-emerald-600">{r.five_star_pct}% 5-star</span>
                <span className="text-red-600">{r.one_star_pct}% 1-star</span>
                <span>Velocity: {r.review_velocity}/week</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'qa' && (
        <div className="space-y-3">
          {questions.map((q) => (
            <div key={q.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(q.status)}`}>{q.status}</span>
                {q.sku && <span className="text-xs text-slate-400">{q.sku}</span>}
              </div>
              <p className="mt-1 text-sm font-medium text-slate-900">{q.question}</p>
              {q.suggested_answer && q.status === 'pending' && (
                <div className="mt-2 rounded-lg bg-violet-50 p-3 text-sm text-violet-800">
                  <span className="font-medium">AI Suggested Answer: </span>{q.suggested_answer}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'vine' && (
        <div className="space-y-3">
          {vine.map((v) => (
            <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{v.product_name}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(v.status)}`}>{v.status.replace(/_/g, ' ')}</span>
              </div>
              <div className="mt-2 flex gap-4 text-sm text-slate-600">
                <span>{v.reviews_received}/{v.reviews_target} reviews</span>
                <span>{v.completion_pct.toFixed(0)}% complete</span>
                {v.estimated_roi != null && v.estimated_roi > 0 && <span>ROI: {v.estimated_roi}x</span>}
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-100">
                <div className="h-2 rounded-full bg-violet-500" style={{ width: `${v.completion_pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'followers' && (
        <div className="space-y-3">
          {followers.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{f.brand_name}</h3>
              <div className="mt-2 flex gap-4 text-sm text-slate-600">
                <span className="text-lg font-semibold">{f.follower_count.toLocaleString()}</span>
                <span>followers</span>
                <span className="text-emerald-600">+{f.growth_rate}% growth</span>
                <span>{(f.engagement_rate * 100).toFixed(1)}% engagement</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'campaigns' && (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{c.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(c.status)}`}>{c.status}</span>
                <span className="text-xs text-slate-400">{c.type?.replace(/_/g, ' ')}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                <span>Audience: {c.audience_size.toLocaleString()}</span>
                <span>Open: {(c.open_rate * 100).toFixed(0)}%</span>
                <span>Click: {(c.click_rate * 100).toFixed(0)}%</span>
                <span>Conv: {(c.conversion_rate * 100).toFixed(1)}%</span>
                {c.revenue_attribution != null && c.revenue_attribution > 0 && (
                  <span>Revenue: ${c.revenue_attribution.toLocaleString()}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'health' && (
        <div className="space-y-3">
          {health.map((h) => (
            <div key={h.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{h.product_name}</span>
                <span className={`font-medium ${scoreClass(h.customer_health_score)}`}>{h.customer_health_score.toFixed(0)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(h.severity)}`}>{h.severity}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                <span>Loyalty: {h.loyalty_score.toFixed(0)}</span>
                <span>Review Health: {h.review_health_score.toFixed(0)}</span>
                <span>Engagement: {h.engagement_score.toFixed(0)}</span>
                <span>Trust: {h.trust_score.toFixed(0)}</span>
                <span>Repeat: {h.repeat_purchase_score.toFixed(0)}</span>
              </div>
              {h.issue && <p className="mt-1 text-sm text-slate-600">{h.issue}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={generateRecs} disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">#{r.priority_rank}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                <span>{r.type.replace(/_/g, ' ')}</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.expected_impact && <span>Impact: {r.expected_impact}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
